import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import React, { useMemo } from 'react';

// Type for the props of the FormikRadioGroup component
interface FormikRadioGroupProps<T extends object, K extends keyof T & string> {
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;
  name: K;
  options: Record<string, string>;
  helpText?: string;
  disabled?: boolean;
  label?: string;
  dataTestId?: string;
}

/**
 * A generic Formik component for rendering a group of radio buttons
 */
const FormikRadioGroup = <
  T extends Record<K, string>,
  K extends keyof T & string,
>(
  props: FormikRadioGroupProps<T, K>
) => {
  const { formik, name, options, helpText, disabled, label } = props;

  /**
   * Any errors with the form field
   */
  const errors = useMemo(() => {
    const formikErrors = formik.errors[name];
    return formikErrors as string | undefined;
  }, [formik, name]);

  /**
   * If the radio group form field has been touched
   */
  const touched = useMemo(() => {
    return formik.touched[name];
  }, [formik, name]);

  /**
   * Handle when the user changes the selected radio button
   * @param event The change event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    formik.setFieldValue(name, value);
  };

  return (
    <FormControl
      component="fieldset"
      disabled={formik.isSubmitting || disabled}
    >
      {label && <FormLabel component="legend">{label}</FormLabel>}
      <RadioGroup
        data-testid={props.dataTestId}
        name={name}
        value={formik.values[name]}
        onChange={handleChange}
      >
        {Object.keys(options).map((key) => (
          <FormControlLabel
            key={key}
            value={key}
            control={<Radio />}
            label={options[key]}
          />
        ))}
      </RadioGroup>
      <FormHelperText error={Boolean(touched && errors)}>
        {(touched && errors) ?? helpText}
      </FormHelperText>
    </FormControl>
  );
};

export default FormikRadioGroup;
